import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import axios, { AxiosError } from "axios";
import { IUserCredentials } from "shared/models/IUserCredentials";
import { ILoginCredentials } from "shared/models/ILoginCredentials";
import { UserRoles } from "./UserRoles";
import jwtDecode from "jwt-decode";
import { UserPermission } from "shared/enums/UserPermissions";
import { isMobile } from "react-device-detect";
import { createURLSearchParams } from "shared/utils/searchParams";

export type IAuthState = IUserCredentials & {
	hasUserSeenReleaseNotes: boolean;
};
const INITIAL_STATE: IAuthState = {
	userId: "",
	isLoggedIn: false,
	email: "",
	userRole: undefined,
	allRoles: [],
	firstName: "",
	lastName: "",
	accessToken: "",
	refreshToken: "",
	organizationId: undefined,
	organizationName: undefined,
	practiceId: undefined,
	practiceName: undefined,
	permissions: [],
	tokenExpiresOn: undefined,
	authType: "local",
	hasUserSeenReleaseNotes: false,
};

export const microsoftAuthLogin = createAsyncThunk("auth/mslogin", async (code: string | null) => {
	let url = "api/Authenticate/currentUser";

	if (!!code) {
		const urlParams = `?${createURLSearchParams({ code }).toString()}`;
		url = url + urlParams;
	}
	const { data } = await axios.get<ILoginResponse>(url);
	return data;
});

export const localAuthLogin = createAsyncThunk(
	"auth/login",
	(userCredentials: ILoginCredentials, thunkAPI) => thunkHandler(postLogin(userCredentials), thunkAPI)
);

const postLogin = (payload: ILoginCredentials) =>
	axios.post<ILoginResponse>("api/Authenticate/login", payload);

export const logout = createAsyncThunk("auth/logout", async () => {
	const response = await axios.post("api/Authenticate/logout");
	return response.data;
});

export const thunkHandler = async (asyncFn, thunkAPI) => {
	try {
		const response = await asyncFn;
		return response.data;
	} catch (error) {
		const response = (error as AxiosError).response;
		return thunkAPI.rejectWithValue(response?.data);
	}
};

const authSlice = createSlice({
	name: "auth",
	initialState: INITIAL_STATE,
	reducers: {
		setTokens: (
			state,
			{
				payload: { accessToken, refreshToken, type, expiresOn },
			}: PayloadAction<IRefreshTokenCredentials>
		) => {
			state.accessToken = accessToken;
			if (expiresOn) state.tokenExpiresOn = expiresOn;
			else {
				const decoded = jwtDecode(accessToken);
				if (
					typeof decoded === "object" &&
					decoded &&
					"exp" in decoded &&
					typeof decoded.exp === "number"
				)
					state.tokenExpiresOn = decoded.exp * 1000;
			}
			state.refreshToken = refreshToken;
			state.authType = type;
		},
		setName: (
			state,
			action: {
				payload: {
					firstName: string;
					lastName: string;
				};
			}
		) => {
			state.firstName = action.payload.firstName;
			state.lastName = action.payload.lastName;
		},
		setRole: (state, action: { payload: { role: UserRoles } }) => {
			state.userRole = action.payload.role;
		},
		setRoles(state, { payload: { roles } }: PayloadAction<{ roles: UserRoles[] }>) {
			state.allRoles = roles.sort();
			if (state.userRole && !roles.includes(state.userRole))
				state.userRole = Math.min(...state.allRoles);
		},
		setOrganization: (
			state,
			action: { payload: { organizationId: string; organizationName: string } }
		) => {
			state.organizationId = action.payload.organizationId;
			state.organizationName = action.payload.organizationName;
		},
		setPractice: (state, action: { payload: { practiceId: string } }) => {
			state.practiceId = action.payload.practiceId;
		},
		markReleaseNotesAsSeen(state, action: PayloadAction<void>) {
			state.hasUserSeenReleaseNotes = true;
		},
	},
	extraReducers: (builder) => {
		builder.addCase(localAuthLogin.fulfilled, (state, action: PayloadAction<ILoginResponse>) => {
			state.userId = action.payload.userId;
			state.isLoggedIn = true;
			state.email = action.payload.email;
			state.userRole =
				isMobile && action.payload.allRoles.includes(UserRoles.PracticeUser)
					? UserRoles.PracticeUser
					: parseInt(action.payload.userRole);
			state.allRoles = [...action.payload.allRoles].sort((a, b) => a - b);
			state.firstName = action.payload.firstName;
			state.lastName = action.payload.lastName;
			state.organizationId = action.payload.organizationId;
			state.organizationName = action.payload.organizationName;
			state.accessToken = action.payload.accessToken;
			state.refreshToken = action.payload.refreshToken;
			state.authType = "local";
			state.practiceId = action.payload.practiceId;
			state.practiceName = action.payload.practiceName;
			state.permissions = action.payload.permissions;
			state.hasUserSeenReleaseNotes = action.payload.hasUserSeenReleaseNotes;
		});
		builder.addCase(microsoftAuthLogin.fulfilled, (state, action: PayloadAction<ILoginResponse>) => {
			state.userId = action.payload.userId;
			state.isLoggedIn = true;
			state.email = action.payload.email;
			state.userRole =
				isMobile && action.payload.allRoles.includes(UserRoles.PracticeUser)
					? UserRoles.PracticeUser
					: parseInt(action.payload.userRole);
			state.allRoles = [...action.payload.allRoles].sort((a, b) => a - b);
			state.firstName = action.payload.firstName;
			state.lastName = action.payload.lastName;
			state.organizationId = action.payload.organizationId;
			state.organizationName = action.payload.organizationName;
			state.practiceId = action.payload.practiceId;
			state.practiceName = action.payload.practiceName;
			state.permissions = action.payload.permissions;
			state.hasUserSeenReleaseNotes = action.payload.hasUserSeenReleaseNotes;
		});
		builder.addCase(
			logout.fulfilled,
			(state, action: PayloadAction<ILoginResponse>) => (state = INITIAL_STATE)
		);
	},
});

interface IRefreshTokenCredentials {
	accessToken: string;
	refreshToken: string;
	expiresOn?: number;
	type: "microsoft" | "local";
}

export interface ILoginResponse {
	userId: string;
	email: string;
	firstName: string;
	lastName: string;
	userRole: string;
	allRoles: UserRoles[];
	accessToken: string;
	refreshToken: string;
	organizationId?: string;
	organizationName?: string;
	practiceId?: string;
	practiceName?: string;
	permissions: UserPermission[];
	hasUserSeenReleaseNotes: boolean;
	redirect: ILoginRedirect;
}

export interface ILoginRedirect {
	url: string;
	info: string;
}

export const {
	setTokens,
	setName,
	setRole,
	setRoles,
	setOrganization,
	setPractice,
	markReleaseNotesAsSeen,
} = authSlice.actions;

export default authSlice.reducer;
